import { resources } from "@constants/hardcoded-strings";
import { MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_URL } from "@constants/routes";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ServiceTemplateMetadataContainer } from "@containers/management-console/service-template-details/service-template-details-component/service-template-metadata-container/service-template-metadata-container";
import { ButtonTypes } from "@enums/button-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { ServiceTemplateDetailsTabTypes } from "@enums/service-template-details-tab-types";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import { FormattedServiceTemplateDetails } from "@idw-types/data/service-template-details/service-template-details";
import { navigateToUtil } from "@utils/navigation-util";
import isEqual from "lodash/isEqual";
import { FormEvent, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Tab from "react-bootstrap/Tab";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import Tabs from "react-bootstrap/Tabs";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./service-template-details-component.less";
import { updateServiceTemplateReduxRequest } from "@redux-requests/service-template-details/update-service-template";
import { useDispatch } from "react-redux";
import { getServiceTemplateValidationErrors } from "@utils/validation/service-template/service-template-validation-util";
import { RpServiceTemplate } from "@idw-types/data/reference-data/generic";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { SelectOption } from "@idw-types/common";
import { mapEntityWithNameAndIdToSelectOption } from "@utils/select-option-mapping-util";
import { unFormatServiceTemplateDetails } from "@formatters/service-template-details/service-template-details-formatter";


type ServiceTemplateDetailsComponentProps = {
    serviceTemplateDetails: FormattedServiceTemplateDetails;
    serviceTemplatesMap: Record<string, RpServiceTemplate>;
    axnCompanies: Array<AxnCompany>;
}

export type ServiceTemplateDetailsComponentState = {
    serviceTemplate: FormattedServiceTemplateDetails;
    selectedOrganizationOption: SelectOption;
};

export const ServiceTemplateDetailsComponent = (props: ServiceTemplateDetailsComponentProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const routerLocation = useLocation();

    const { serviceTemplateDetails, serviceTemplatesMap, axnCompanies } = props;
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [labelWidth] = useState(LabelWidthTypes.SMALL);
    const [serviceTemplateDetailsState, setServiceTemplateDetailsState] = useState<ServiceTemplateDetailsComponentState>({
        serviceTemplate: serviceTemplateDetails,
        selectedOrganizationOption: mapEntityWithNameAndIdToSelectOption(serviceTemplateDetails.organization)
    });

    const stateIsUnchanged = useMemo(() => {
        return isEqual(serviceTemplateDetails, serviceTemplateDetailsState.serviceTemplate);
    }, [serviceTemplateDetails, serviceTemplateDetailsState.serviceTemplate]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-service-template-changes",
        modalType: ModalTypes.DiscardServiceTemplateChanges,
        stateIsUnchanged
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);

        const serviceTemplateDetails = unFormatServiceTemplateDetails(serviceTemplateDetailsState.serviceTemplate);
        const updatedServiceTemplateDetailsResult = await updateServiceTemplateReduxRequest(dispatch)(serviceTemplateDetails, axnCompanies);

        if (updatedServiceTemplateDetailsResult) {
            setServiceTemplateDetailsState({
                ...serviceTemplateDetailsState,
                serviceTemplate: updatedServiceTemplateDetailsResult,
                selectedOrganizationOption: mapEntityWithNameAndIdToSelectOption(updatedServiceTemplateDetailsResult.organization)
            });
        }
        setRequestInProgress(false);
    }

    const onCloseClick = () => {
        navigateToUtil(MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_URL, navigate, routerLocation.pathname);
    };

    const validationErrors = useMemo(() => {
        const serviceTemplateDetails = unFormatServiceTemplateDetails(serviceTemplateDetailsState.serviceTemplate);
        return getServiceTemplateValidationErrors(serviceTemplateDetails, serviceTemplatesMap);
    }, [serviceTemplateDetailsState.serviceTemplate, serviceTemplatesMap]);

    const resource = resources.pages.serviceTemplateDetails;

    return (
        <div className={styles.serviceTemplateDetailsComponent}>
            <Form onSubmit={handleSubmit}>
                <div className={styles.headerContainer}>
                    <div className={styles.headerRow}>
                        <span className={styles.serviceTemplateName}>{serviceTemplateDetailsState.serviceTemplate.name}</span>

                        <div className={styles.buttonContainer}>
                            <Button id="service-template-details-close-button" onClick={onCloseClick} variant={"outline-secondary"}>{resources.common.buttons.close.label}</Button>
                            <ButtonWithSpinner
                                id="service-template-details-save-button"
                                disabled={requestInProgress || stateIsUnchanged || validationErrors.count > 0}
                                hideSpinner={stateIsUnchanged || validationErrors.count > 0}
                                variant="primary"
                                type={ButtonTypes.SUBMIT}
                                label={resources.common.buttons.save.label}
                            />
                        </div>
                    </div>
                </div>
                <TabContainer>
                    {/* @ts-ignore */}
                    <Tabs id="service-template-details" fill={false} unmountOnExit={true}>
                        <Tab eventKey={ServiceTemplateDetailsTabTypes.METADATA} title={validationErrors.metadata.tabTitle}>
                            <ContentPageHeader title={resource.tabs.metadata.header} />
                            <ContentPageBody isMaxWidth={true}>
                                <ServiceTemplateMetadataContainer
                                    isDisabled={requestInProgress}
                                    axnCompanies={axnCompanies}
                                    serviceTemplateDetailsState={serviceTemplateDetailsState}
                                    setServiceTemplateDetailsState={setServiceTemplateDetailsState}
                                    labelWidth={labelWidth}
                                    validationErrors={validationErrors.metadata}
                                />
                            </ContentPageBody>
                        </Tab>
                    </Tabs>
                </TabContainer>
            </Form>
        </div>
    );
}